// Frameworks admin API wrapper — tip-safe fetch'ler.
import type {
  CreateFrameworkRequest,
  CreateFrameworkVersionRequest,
  CreateQuestionRequest,
  CreateSectionRequest,
  FrameworkDetail,
  FrameworkVersionEditabilityResponse,
  FrameworkVersionSummary,
  FrameworkVersionTreeResponse,
  FrameworksListResponse,
  PeriodType,
  PublishValidationResult,
  PublishVersionResponse,
  Question,
  Section,
  SectorBlocksResponse,
  UpdateFrameworkPromptRequest,
  UpdateFrameworkRequest,
  UpdateQuestionRequest,
  UpdateSectionRequest,
} from "@mahir/shared";
import { api } from "./client";

// M13 S7: published version'ın edit/delete'e uygun olup olmadığını sorgular.
export interface VersionEditabilityResponse extends FrameworkVersionEditabilityResponse {
  status: "draft" | "published" | "archived";
}

export async function getVersionEditability(
  frameworkId: string,
  versionId: string,
): Promise<VersionEditabilityResponse> {
  return api<VersionEditabilityResponse>(
    `/admin/frameworks/${frameworkId}/versions/${versionId}/editability`,
  );
}

export async function listFrameworks(): Promise<FrameworksListResponse> {
  return api<FrameworksListResponse>("/admin/frameworks");
}

export async function getFramework(id: string): Promise<FrameworkDetail> {
  const res = await api<{ framework: FrameworkDetail }>(`/admin/frameworks/${id}`);
  return res.framework;
}

export async function createFramework(body: CreateFrameworkRequest): Promise<FrameworkDetail> {
  const res = await api<{ framework: FrameworkDetail }>("/admin/frameworks", {
    method: "POST",
    body,
  });
  return res.framework;
}

export async function updateFramework(
  id: string,
  body: UpdateFrameworkRequest,
): Promise<FrameworkDetail> {
  const res = await api<{ framework: FrameworkDetail }>(`/admin/frameworks/${id}`, {
    method: "PATCH",
    body,
  });
  return res.framework;
}

// #008 Soft delete framework. 409 in_use atılırsa kullanıcıya hata gösterilir.
export async function deleteFramework(id: string): Promise<void> {
  await api(`/admin/frameworks/${id}`, { method: "DELETE" });
}

// #023 Soft delete framework version (fork). 409: not_draft / in_use / last_version.
export async function deleteFrameworkVersion(
  frameworkId: string,
  versionId: string,
): Promise<void> {
  await api(`/admin/frameworks/${frameworkId}/versions/${versionId}`, { method: "DELETE" });
}

export async function forkFrameworkVersion(
  frameworkId: string,
  body: CreateFrameworkVersionRequest = {},
): Promise<FrameworkVersionSummary> {
  const res = await api<{ version: FrameworkVersionSummary }>(
    `/admin/frameworks/${frameworkId}/versions`,
    { method: "POST", body },
  );
  return res.version;
}

// M-L2: framework-level L2 prompt güncelle (her zaman editlenebilir, draft/published fark etmez).
export async function patchFrameworkPrompt(
  frameworkId: string,
  body: UpdateFrameworkPromptRequest,
): Promise<{ framework: { frameworkPrompt: string; updatedAt: string } }> {
  return api(`/admin/frameworks/${frameworkId}/prompt`, { method: "PATCH", body });
}

export async function getVersionTree(
  frameworkId: string,
  versionId: string,
): Promise<FrameworkVersionTreeResponse> {
  return api<FrameworkVersionTreeResponse>(
    `/admin/frameworks/${frameworkId}/versions/${versionId}/tree`,
  );
}

// Sections
export async function createSection(
  frameworkId: string,
  versionId: string,
  body: CreateSectionRequest,
): Promise<{ section: Section; newVersionUpdatedAt: string }> {
  return api(`/admin/frameworks/${frameworkId}/versions/${versionId}/sections`, {
    method: "POST",
    body,
  });
}

export async function updateSection(
  id: string,
  body: UpdateSectionRequest,
): Promise<{ section: Section; newVersionUpdatedAt: string }> {
  return api(`/admin/sections/${id}`, { method: "PATCH", body });
}

export async function deleteSection(
  id: string,
  expectedVersionUpdatedAt: string,
): Promise<{ newVersionUpdatedAt: string }> {
  return api(`/admin/sections/${id}`, {
    method: "DELETE",
    body: { expectedVersionUpdatedAt },
  });
}

// Questions
export async function createQuestion(
  sectionId: string,
  body: CreateQuestionRequest,
): Promise<{ question: Question; newVersionUpdatedAt: string }> {
  return api(`/admin/sections/${sectionId}/questions`, { method: "POST", body });
}

export async function updateQuestion(
  id: string,
  body: UpdateQuestionRequest,
): Promise<{ question: Question; newVersionUpdatedAt: string }> {
  return api(`/admin/questions/${id}`, { method: "PATCH", body });
}

export async function deleteQuestion(
  id: string,
  expectedVersionUpdatedAt: string,
): Promise<{ newVersionUpdatedAt: string }> {
  return api(`/admin/questions/${id}`, {
    method: "DELETE",
    body: { expectedVersionUpdatedAt },
  });
}

export async function validatePublish(
  frameworkId: string,
  versionId: string,
): Promise<PublishValidationResult> {
  return api(`/admin/frameworks/${frameworkId}/versions/${versionId}/validate-publish`);
}

export async function publishVersion(
  frameworkId: string,
  versionId: string,
  /** Modalde işaretlenen `SEK-*` bölüm kodları; boş/undefined → türetme yok. */
  deriveSectorPackages?: readonly string[],
): Promise<PublishVersionResponse> {
  return api(`/admin/frameworks/${frameworkId}/versions/${versionId}/publish`, {
    method: "POST",
    body:
      deriveSectorPackages && deriveSectorPackages.length > 0
        ? { deriveSectorPackages: [...deriveSectorPackages] }
        : {},
  });
}

/** Yayınla modalı — sürümdeki `SEK-*` blokları ve türetilecek paket adları. */
export async function listSectorBlocks(
  frameworkId: string,
  versionId: string,
): Promise<SectorBlocksResponse> {
  return api(`/admin/frameworks/${frameworkId}/versions/${versionId}/sector-blocks`);
}

/**
 * FW-04 — hedefli (mevcut framework'e yeni sürüm) import'ta dolu olan hedef + fark özeti.
 * Hedef çözüldüyse backend bunu HATA dönüşlerinde de gönderir (şema hatasında fark listeleri boş).
 */
export interface ImportTargetSummary {
  frameworkId: string;
  frameworkName: string;
  /** Taban alınan en güncel sürümün numarası — framework'ün hiç sürümü yoksa null. */
  baseVersionNumber: string | null;
  added: string[];
  removed: string[];
  typeChanged: string[];
  /**
   * K7 D3 — bölüm ("sec") veya soru ("sec/q") kimliğiyle dönem değişikliği. F1: optional —
   * backend her zaman doldurur ama eski/test literal'ları alan olmadan da derlensin.
   */
  periodChanged?: { key: string; before: PeriodType[]; after: PeriodType[] }[];
  /** K7 D3 — framework.supportsQuarterly değişikliği; değişmemişse null. F1: optional. */
  supportsQuarterlyChanged?: { before: boolean; after: boolean } | null;
}

export interface ImportResult {
  frameworkId: string | null;
  /** FW-04 — yazılan yeni sürümün id'si; dryRun ve hata yollarında null. */
  versionId: string | null;
  summary: {
    frameworkName: string;
    sectionCount: number;
    questionCount: number;
    /** M15 — dosyadaki bileşik (composite) soru sayısı */
    compositeCount: number;
    /** Dosyadaki `SEK-*` bölüm kodları — önizlemede türetme bilgisi satırı. */
    sectorBlocks: string[];
    nameCollision: boolean;
    target?: ImportTargetSummary;
  };
  errors: string[];
  /** Kit C3 — bloklamayan uyarılar (bilinmeyen/yazım hatalı alanlar). */
  warnings: string[];
}

// Export — JSON'u blob olarak indir. api() JSON parse ettiği için fetch'i doğrudan kullanırız.
export async function exportFrameworkVersion(
  frameworkId: string,
  versionId: string,
): Promise<void> {
  const res = await fetch(`/api/v1/admin/frameworks/${frameworkId}/versions/${versionId}/export`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error("Export başarısız");
  const disposition = res.headers.get("content-disposition") ?? "";
  const match = /filename="([^"]+)"/.exec(disposition);
  const filename = match?.[1] ?? "framework-export.json";
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export async function importFrameworkDryRun(payload: unknown): Promise<ImportResult> {
  return api<ImportResult>(`/admin/frameworks/import?dryRun=true`, {
    method: "POST",
    body: { export: payload },
  });
}

export async function importFrameworkCommit(payload: unknown): Promise<ImportResult> {
  return api<ImportResult>(`/admin/frameworks/import`, {
    method: "POST",
    body: { export: payload },
  });
}

// FW-04 — mevcut framework'e YENİ TASLAK SÜRÜM olarak içe aktarma. Framework adı dosyadan
// değişmez; 409 draft_exists / version_conflict ApiError olarak yükselir (TransferMenu ipucu verir).
export async function importVersionDryRun(
  frameworkId: string,
  payload: unknown,
): Promise<ImportResult> {
  return api<ImportResult>(`/admin/frameworks/${frameworkId}/versions/import?dryRun=true`, {
    method: "POST",
    body: { export: payload },
  });
}

export async function importVersionCommit(
  frameworkId: string,
  payload: unknown,
): Promise<ImportResult> {
  return api<ImportResult>(`/admin/frameworks/${frameworkId}/versions/import`, {
    method: "POST",
    body: { export: payload },
  });
}
